"use client";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Ico18DoubleArrowRight } from "@/shared/assets/icons/ico_18_double_arrow_right";
import { cn } from "@/shared/lib";
import { Ico24File } from "@/shared/assets/icons/ico_24_file";
import { useRouter } from "next/navigation";
import { GetOpenNoticeListItem } from "@/server-action/notice/get-open-notice-list";
import DateCell from "@/shared/ui/date-cell";
import { useTranslations } from "next-intl";

const PAGE_SIZE = 10;
const PAGE_GROUP_SIZE = 5;

interface NoticeTableProps {
  notices: GetOpenNoticeListItem[];
  totalItems: number;
  currentPage: number;
}

export function NoticeTable({ notices, totalItems, currentPage }: NoticeTableProps) {
  const router = useRouter();
  const page = currentPage;
  const t = useTranslations();

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentGroup = Math.ceil(page / PAGE_GROUP_SIZE);
  const startPage = (currentGroup - 1) * PAGE_GROUP_SIZE + 1;
  const endPage = Math.min(startPage + PAGE_GROUP_SIZE - 1, totalPages);

  return (
    <div className="page-container flex flex-col items-center">
      <div className="w-full max-w-[1280px]">
        <h1 className="heading-1 text-gray-900">{t("f_nt_title")}</h1>

        <div className="tb:mt-20 mt-15">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="h-17 border-gray-900">
                <TableHead className="body-lg-bold tb:w-[34px] w-16 text-center" variant="public">
                  {t("f_nt_list_header_no")}
                </TableHead>
                <TableHead className="body-lg-bold tb:py-6 py-3 text-center" variant="public">
                  {t("f_nt_list_header_name")}
                </TableHead>
                <TableHead
                  className="body-lg-bold tb:py-6 tb:w-50 tb:whitespace-normal w-[71px] py-3 text-center whitespace-pre-line"
                  variant="public"
                >
                  {t("f_nt_list_header_file")}
                </TableHead>
                <TableHead className="body-lg-bold tb:py-6 tb:table-cell hidden w-50 py-3 text-center" variant="public">
                  {t("f_nt_list_header_date")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notices.length === 0 ? (
                <TableRow className="border-gray-100">
                  <TableCell
                    colSpan={4}
                    className="body-md-regular tb:h-[480px] h-[222px] px-0 py-25 text-center text-gray-400"
                  >
                    {t("f_nt_list_null")}
                  </TableCell>
                </TableRow>
              ) : (
                notices.map((notice, index) => (
                  <TableRow
                    key={notice.id}
                    className="cursor-pointer border-gray-100 py-[14px] hover:bg-gray-50"
                    onClick={() => router.push(`/notice/${notice.id}`)}
                  >
                    <TableCell className="body-md-regular pc:w-[34px] w-[50px] p-0 text-center text-gray-500">
                      {totalItems - (page - 1) * PAGE_SIZE - index}
                    </TableCell>

                    <TableCell className="body-lg-medium tb:py-6 tb:pr-0 tb:pl-15 truncate py-[14px] pr-0 pl-[10px] text-gray-900">
                      <div className="tb:hidden truncate">
                        {notice.title}
                        <span className="body-md-regular block text-gray-500">
                          {notice.createdAt.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="tb:block hidden truncate">{notice.title}</div>
                    </TableCell>

                    <TableCell className="body-md-regular tb:px-12 tb:py-6 px-5 text-center text-gray-500">
                      <div className="flex h-full items-center justify-center">
                        {notice.attachmentFilename ? <Ico24File className="tb:size-6 size-5" /> : null}
                      </div>
                    </TableCell>

                    <TableCell className="body-md-regular tb:table-cell hidden px-0 py-6 text-center text-gray-500">
                      <DateCell date={notice.createdAt} dateOnly />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {notices.length > 0 && (
            <div className="tb:mt-15 mt-5 flex justify-center">
              <Pagination>
                <PaginationContent>
                  {/* << 버튼 (이전 페이지 그룹) */}
                  <PaginationItem>
                    <PaginationLink
                      href={startPage > 1 ? `?page=${startPage - 1}` : "#"}
                      className={cn(
                        "pc:mr-2 tb:mr-3 mr-1",
                        startPage <= 1 ? "pointer-events-none text-gray-300" : "text-gray-700",
                      )}
                    >
                      <Ico18DoubleArrowRight className="tb:size-6 pc:size-[18px] size-5 rotate-180" />
                    </PaginationLink>
                  </PaginationItem>

                  <PaginationItem>
                    <PaginationPrevious
                      href={page > 1 ? `?page=${page - 1}` : "#"}
                      className={cn(page <= 1 ? "pointer-events-none text-gray-300" : "text-gray-700")}
                    />
                  </PaginationItem>

                  {/* 페이지 번호 버튼 */}
                  <div className="pc:mx-6 tb:mx-8 tb:gap-3 pc:gap-2 mx-[10px] flex gap-1">
                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((pageNum) => (
                      <PaginationItem key={pageNum}>
                        <PaginationLink href={`?page=${pageNum}`} isActive={page === pageNum}>
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                  </div>

                  <PaginationItem>
                    <PaginationNext
                      href={page < totalPages ? `?page=${page + 1}` : "#"}
                      className={cn(page >= totalPages ? "pointer-events-none text-gray-300" : "text-gray-700")}
                    />
                  </PaginationItem>

                  {/* >> 버튼 (다음 페이지 그룹) */}
                  <PaginationItem>
                    <PaginationLink
                      href={endPage < totalPages ? `?page=${endPage + 1}` : "#"}
                      className={cn(
                        "pc:ml-2 tb:ml-3 ml-1",
                        endPage >= totalPages ? "pointer-events-none text-gray-300" : "text-gray-700",
                      )}
                    >
                      <Ico18DoubleArrowRight className="tb:size-6 pc:size-[18px] size-5" />
                    </PaginationLink>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
